<template>
  <div>
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 resource-title">Salary Tax Slabs</h5>
          <small class="text-muted">
            Annual salary brackets — tax = base Rs + rate% × (salary − from Rs)
          </small>
        </div>
      </div>

      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="page-size-wrapper">
          <label class="page-size-label">Show:</label>
          <v-select
            v-model="perPage"
            :options="pageSizeOptions"
            :reduce="(option) => option.value"
            label="label"
            :clearable="false"
            :searchable="false"
            append-to-body
            class="page-size-select-compact"
            @input="onPageSizeChange"
          />
        </div>

        <div class="pagination-count-display slab-pagination-meta">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
          <span v-if="hasPeriodFilter" class="slab-filter-period-chip">
            Period:
            <strong>{{ filterPeriodLabel }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="loadSlabs"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center flex-wrap">
          <div class="slab-filter-bar d-flex align-items-end flex-wrap">
            <div class="slab-filter-field slab-filter-field--search">
              <label class="slab-filter-label">Search</label>
              <b-form-input
                v-model="searchQuery"
                class="filter-search-input"
                placeholder="Search slabs..."
                @input="onSearchChange"
              />
            </div>

            <div class="slab-filter-field">
              <label class="slab-filter-label">Period from</label>
              <div class="slab-date-wrap">
                <flat-pickr
                  v-model="filters.dateFrom"
                  :config="dateConfig"
                  class="form-control slab-date-input"
                  placeholder="From date"
                  @on-change="onPeriodFilterChange"
                />
              </div>
            </div>

            <div class="slab-filter-field">
              <label class="slab-filter-label">Period to</label>
              <div class="slab-date-wrap">
                <flat-pickr
                  v-model="filters.dateTo"
                  :config="dateConfig"
                  class="form-control slab-date-input"
                  placeholder="To date"
                  @on-change="onPeriodFilterChange"
                />
              </div>
            </div>

            <b-button
              v-if="filters.dateFrom || filters.dateTo || searchQuery"
              variant="outline-secondary"
              class="filter-clear-btn action-btn-filter slab-filter-clear"
              v-b-tooltip.hover.top="'Clear filters'"
              @click="clearFilters"
            >
              <span class="filter-clear-x">×</span>
            </b-button>

            <div class="slab-filter-actions ml-auto">
              <b-button
                v-if="rights.edit || rights.add"
                variant="outline-primary"
                class="action-btn-filter text-nowrap"
                :disabled="!hasPeriodFilter || bulkLoading"
                v-b-tooltip.hover.top="
                  hasPeriodFilter
                    ? 'Edit all slabs in the selected period'
                    : 'Select Period from and Period to first'
                "
                @click="openBulkModal()"
              >
                <b-spinner v-if="bulkLoading" small class="mr-50" />
                <feather-icon v-else icon="EditIcon" class="mr-50" />
                <span>{{
                  rights.edit ? "Edit slabs for period" : "Add slabs for period"
                }}</span>
              </b-button>
            </div>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <b-table
          class="planner-list-table mt-1"
          :items="displaySlabs"
          :fields="fields"
          :busy="dataLoading"
          show-empty
          :responsive="false"
          hover
          small
        >
          <template #empty>
            <div v-if="!dataLoading" class="empty-slabs text-center py-3">
              <p class="text-muted mb-2">No slabs for this period yet.</p>
              <b-button
                v-if="(rights.add || rights.edit) && hasPeriodFilter"
                variant="outline-primary"
                size="sm"
                @click="openBulkModal()"
              >
                <feather-icon icon="PlusIcon" size="14" class="mr-50" />
                Add slabs for period
              </b-button>
            </div>
          </template>

          <template #table-busy>
            <div class="list-skeleton">
              <div
                v-for="n in 5"
                :key="'skeleton-' + n"
                class="list-skeleton__row"
                :style="{
                  gridTemplateColumns: `repeat(${fields.length}, minmax(0, 1fr))`,
                }"
              >
                <div
                  v-for="(field, index) in fields"
                  :key="'skeleton-cell-' + n + '-' + index"
                  class="list-skeleton__cell"
                >
                  <div
                    class="list-skeleton__bar"
                    :style="{ width: skeletonBarWidth(index) }"
                  ></div>
                </div>
              </div>
            </div>
          </template>

          <template #cell(index)="data">
            {{ (currentPage - 1) * perPage + data.index + 1 }}
          </template>

          <template #cell(period)="{ item }">
            <span class="slab-period-text">
              {{ formatDisplayDate(slabDateFrom(item)) }}
              <span class="text-muted mx-25">→</span>
              {{ formatDisplayDate(slabDateTo(item)) || "Open" }}
            </span>
          </template>

          <template #cell(incomeFrom)="{ item }">
            {{ formatMoney(slabIncomeFrom(item)) }}
          </template>

          <template #cell(incomeTo)="{ item }">
            {{
              slabIncomeTo(item) == null || slabIncomeTo(item) === ""
                ? "Above"
                : formatMoney(slabIncomeTo(item))
            }}
          </template>

          <template #cell(taxRate)="{ item }">
            <b-badge variant="light-warning">
              {{ formatRate(slabTaxRate(item)) }}
            </b-badge>
          </template>

          <template #cell(fixedTax)="{ item }">
            {{ formatMoney(slabFixedTax(item)) }}
          </template>

          <template #cell(isActive)="{ item }">
            <b-form-checkbox
              switch
              :checked="!!item.isActive"
              :disabled="!rights.edit || statusLoadingId === item.id"
              @change="(val) => toggleStatus(item, val)"
            />
          </template>

          <template #head(actions)>
            <div class="text-center">Actions</div>
          </template>

          <template #cell(actions)="{ item }">
            <div
              v-if="rights.edit || rights.delete"
              class="d-flex justify-content-center"
            >
              <b-button
                v-if="rights.edit"
                variant="gradient-primary"
                v-b-tooltip.hover.top
                title="Edit"
                class="action-btn-table mr-1"
                @click="editSlab(item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                v-b-tooltip.hover.top
                title="Delete"
                class="action-btn-outline delete-btn"
                @click="deleteSlab(item)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
            <span v-else class="text-muted">—</span>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Single slab modal (quick edit from grid) -->
    <b-modal
      v-model="showModal"
      centered
      no-close-on-backdrop
      size="md"
      modal-class="slab-modal slab-modal--edit"
      body-class="slab-modal-body"
      header-class="slab-modal-header"
      footer-class="slab-modal-footer"
      @hidden="resetForm"
    >
      <template #modal-title>
        <span class="slab-modal-title">Edit tax slab</span>
      </template>

      <b-form class="slab-form" @submit.prevent>
        <p class="slab-hint mb-2">
          Tax = base Rs + rate% × (annual salary − from Rs)
        </p>
        <b-row class="mb-1">
          <b-col md="6">
            <b-form-group label="Period from *" class="mb-2">
              <flat-pickr
                v-model="form.dateFrom"
                :config="dateConfig"
                class="form-control slab-field-input"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Period to *" class="mb-2">
              <flat-pickr
                v-model="form.dateTo"
                :config="dateConfig"
                class="form-control slab-field-input"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group label="Annual salary from (Rs) *" class="mb-2">
          <b-form-input
            v-model.number="form.incomeFrom"
            type="number"
            min="0"
            class="slab-field-input"
          />
        </b-form-group>
        <b-form-group label="Annual salary to (Rs)" class="mb-2">
          <b-form-input
            :value="incomeToInput(form.incomeTo)"
            type="number"
            min="0"
            class="slab-field-input"
            placeholder="Empty = no upper limit"
            @input="(v) => setIncomeToForm(v)"
          />
        </b-form-group>
        <b-row>
          <b-col md="6">
            <b-form-group label="Base tax (Rs)" class="mb-2">
              <b-form-input
                v-model.number="form.fixedTax"
                type="number"
                min="0"
                class="slab-field-input"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Rate on excess (%) *" class="mb-2">
              <b-form-input
                v-model.number="form.taxRate"
                type="number"
                min="0"
                max="100"
                class="slab-field-input"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-checkbox v-model="form.isActive" switch>
          Active
        </b-form-checkbox>
      </b-form>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false">
          Cancel
        </b-button>
        <b-button variant="primary" :disabled="saving" @click="saveSlab">
          <b-spinner v-if="saving" small />
          {{ isEdit ? "Update" : "Save" }}
        </b-button>
      </template>
    </b-modal>

    <!-- Year editor -->
    <b-modal
      v-model="showBulkModal"
      centered
      no-close-on-backdrop
      hide-header-close
      size="xl"
      modal-class="slab-modal slab-modal--editor"
      body-class="slab-modal-body slab-modal-body--editor p-0"
      header-class="slab-modal-header"
      footer-class="slab-modal-footer"
    >
      <template #modal-header="{ close }">
        <div class="slab-modal-header-row">
          <div class="slab-modal-header-text">
            <span class="slab-modal-title">Salary tax slabs</span>
            <b-badge variant="light-primary" class="slab-year-badge mt-50">
              {{ periodBadgeLabel }}
            </b-badge>
          </div>
          <button
            type="button"
            aria-label="Close"
            class="slab-modal-close-btn"
            @click="close()"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      </template>

      <div class="slab-editor-shell">
        <div class="slab-editor-top">
          <div class="slab-period-row mb-2">
            <div class="slab-period-field">
              <label>Period from *</label>
              <flat-pickr
                v-model="bulkPeriod.dateFrom"
                :config="dateConfig"
                class="form-control slab-field-input"
                placeholder="Start date"
              />
            </div>
            <div class="slab-period-field">
              <label>Period to *</label>
              <flat-pickr
                v-model="bulkPeriod.dateTo"
                :config="dateConfig"
                class="form-control slab-field-input"
                placeholder="End date"
              />
            </div>
          </div>
          <p class="slab-editor-formula mb-0">
            Tax = <strong>base Rs</strong> + <strong>rate%</strong> × (salary −
            from Rs). Leave <strong>Salary to</strong> empty on the last row only.
          </p>
          <div class="slab-editor-toolbar">
            <b-button size="sm" variant="outline-secondary" @click="addBulkRow">
              <feather-icon icon="PlusIcon" size="14" class="mr-25" />
              Add row
            </b-button>
            <span class="slab-row-count text-muted ml-auto">
              {{ bulkRows.length }} row(s)
            </span>
          </div>
        </div>

        <div class="slab-editor-scroll">
          <table class="slab-editor-table">
            <thead>
              <tr>
                <th class="col-num">#</th>
                <th>Salary from (Rs)</th>
                <th>Salary to (Rs)</th>
                <th>Base tax (Rs)</th>
                <th>Rate %</th>
                <th class="col-del"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in bulkRows" :key="'bulk-' + index">
                <td class="col-num">{{ index + 1 }}</td>
                <td>
                  <b-form-input
                    v-model.number="row.incomeFrom"
                    type="number"
                    min="0"
                    size="sm"
                    class="slab-field-input"
                  />
                </td>
                <td>
                  <b-form-input
                    :value="incomeToInput(row.incomeTo)"
                    type="number"
                    min="0"
                    size="sm"
                    class="slab-field-input"
                    placeholder="No limit"
                    @input="(v) => setIncomeToRow(row, v)"
                  />
                </td>
                <td>
                  <b-form-input
                    v-model.number="row.fixedTax"
                    type="number"
                    min="0"
                    size="sm"
                    class="slab-field-input"
                  />
                </td>
                <td>
                  <b-form-input
                    v-model.number="row.taxRate"
                    type="number"
                    min="0"
                    size="sm"
                    class="slab-field-input"
                  />
                </td>
                <td class="col-del">
                  <b-button
                    size="sm"
                    variant="outline-danger"
                    class="action-btn-outline delete-btn"
                    :disabled="bulkRows.length <= 1"
                    @click="removeBulkRow(index)"
                  >
                    <feather-icon icon="Trash2Icon" size="14" />
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showBulkModal = false">
          Cancel
        </b-button>
        <b-button variant="primary" :disabled="bulkSaving" @click="saveBulkSlabs">
          <b-spinner v-if="bulkSaving" small />
          Save {{ bulkRows.length }} slab(s)
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BFormCheckbox,
  BSpinner,
  BBadge,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { mapActions } from "vuex";
import Swal from "sweetalert2";

export default {
  name: "SalaryTaxSlab",
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BFormCheckbox,
    BSpinner,
    BBadge,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      slabs: [],
      searchQuery: "",
      searchTimeout: null,
      filters: {
        dateFrom: "",
        dateTo: "",
      },
      bulkPeriod: {
        dateFrom: "",
        dateTo: "",
      },
      dateConfig: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      currentPage: 1,
      perPage: 20,
      totalCount: 0,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      dataLoading: false,
      saving: false,
      bulkLoading: false,
      bulkSaving: false,
      statusLoadingId: null,
      showModal: false,
      showBulkModal: false,
      isEdit: false,
      bulkRows: [],
      fields: [
        {
          key: "index",
          label: "S.No",
          thClass: "text-center",
          tdClass: "text-center",
        },
        { key: "period", label: "Period" },
        { key: "incomeFrom", label: "Salary from (Rs)" },
        { key: "incomeTo", label: "Salary to (Rs)" },
        { key: "taxRate", label: "Rate on excess" },
        { key: "fixedTax", label: "Base tax (Rs)" },
        { key: "isActive", label: "Active" },
        {
          key: "actions",
          label: "Actions",
          thClass: "text-center",
          tdClass: "text-center",
        },
      ],
      form: {
        id: 0,
        campusID: 0,
        dateFrom: "",
        dateTo: "",
        incomeFrom: 0,
        incomeTo: null,
        taxRate: 0,
        fixedTax: 0,
        sortOrder: 0,
        isActive: true,
      },
      rights: {},
    };
  },
  computed: {
    hasPeriodFilter() {
      return !!(this.filters.dateFrom && this.filters.dateTo);
    },
    periodBadgeLabel() {
      return this.formatPeriodRange(
        this.bulkPeriod.dateFrom,
        this.bulkPeriod.dateTo
      );
    },
    filterPeriodLabel() {
      return this.formatPeriodRange(
        this.filters.dateFrom,
        this.filters.dateTo
      );
    },
    displayedRange() {
      if (this.totalCount === 0) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    displaySlabs() {
      if (!this.searchQuery.trim()) return this.slabs;
      const q = this.searchQuery.trim().toLowerCase();
      return this.slabs.filter((item) => {
        const parts = [
          this.slabDateFrom(item),
          this.slabDateTo(item),
          this.slabIncomeFrom(item),
          this.slabIncomeTo(item),
          this.slabTaxRate(item),
          this.slabFixedTax(item),
        ]
          .join(" ")
          .toLowerCase();
        return parts.includes(q);
      });
    },
  },
  async created() {
    let right = {};
    (this.$store.state.menu || []).forEach((el) => {
      (el.children || []).forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
          assignSections: ch.assignSections,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    await this.loadSlabs();
  },
  beforeDestroy() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    emptyForm(dateFrom, dateTo) {
      return {
        id: 0,
        campusID: 0,
        dateFrom: dateFrom || this.filters.dateFrom || "",
        dateTo: dateTo || this.filters.dateTo || "",
        incomeFrom: 0,
        incomeTo: null,
        taxRate: 0,
        fixedTax: 0,
        sortOrder: 0,
        isActive: true,
      };
    },

    slabDateFrom(item) {
      return (
        item.dateFrom ||
        item.dtFrom ||
        item.effectiveFrom ||
        item.fromDate ||
        item.periodFrom ||
        ""
      );
    },

    slabDateTo(item) {
      return (
        item.dateTo ||
        item.dtTo ||
        item.effectiveTo ||
        item.toDate ||
        item.periodTo ||
        ""
      );
    },

    formatDisplayDate(value) {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    formatPeriodRange(dateFrom, dateTo) {
      if (!dateFrom && !dateTo) return "—";
      const from = dateFrom ? this.formatDisplayDate(dateFrom) : "…";
      const to = dateTo ? this.formatDisplayDate(dateTo) : "…";
      return `${from} → ${to}`;
    },

    appendPeriodQueryParams(url) {
      if (this.filters.dateFrom) {
        url += "&dateFrom=" + encodeURIComponent(this.filters.dateFrom);
      }
      if (this.filters.dateTo) {
        url += "&dateTo=" + encodeURIComponent(this.filters.dateTo);
      }
      return url;
    },

    campusID() {
      return Number(this.$store.state.userData.cId) || 0;
    },

    userID() {
      return (
        Number(this.$store.state.userData.userID) ||
        Number(this.$store.state.userData.id) ||
        0
      );
    },

    apiRoot() {
      return `${this.$store.state.domain}SalaryTaxSlab/`;
    },

    skeletonBarWidth(index) {
      const widths = ["40%", "55%", "70%", "60%", "50%", "45%", "65%", "35%"];
      return widths[index % widths.length];
    },

    slabIncomeFrom(item) {
      return (
        item.incomeFrom ??
        item.minIncome ??
        item.fromAmount ??
        item.lowerLimit ??
        0
      );
    },
    slabIncomeTo(item) {
      const v =
        item.incomeTo ??
        item.maxIncome ??
        item.toAmount ??
        item.upperLimit ??
        null;
      return v === 0 ? null : v;
    },
    slabTaxRate(item) {
      return item.taxRate ?? item.rate ?? item.percentage ?? 0;
    },
    slabFixedTax(item) {
      return item.fixedTax ?? item.fixedAmount ?? item.baseTax ?? 0;
    },
    slabSortOrder(item) {
      return item.sortOrder ?? item.sequence ?? item.slabOrder ?? 0;
    },

    normalizeSlab(item) {
      return {
        id: item.id || 0,
        campusID: item.campusID ?? this.campusID(),
        dateFrom: this.slabDateFrom(item) || this.filters.dateFrom,
        dateTo: this.slabDateTo(item) || this.filters.dateTo,
        incomeFrom: Number(this.slabIncomeFrom(item)) || 0,
        incomeTo:
          this.slabIncomeTo(item) === null || this.slabIncomeTo(item) === ""
            ? null
            : Number(this.slabIncomeTo(item)),
        taxRate: Number(this.slabTaxRate(item)) || 0,
        fixedTax: Number(this.slabFixedTax(item)) || 0,
        sortOrder: Number(this.slabSortOrder(item)) || 0,
        isActive: item.isActive !== false,
      };
    },

    formatMoney(val) {
      const n = Number(val);
      if (Number.isNaN(n)) return "—";
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    formatRate(val) {
      const n = Number(val);
      if (Number.isNaN(n)) return "—";
      return `${n}%`;
    },

    onSearchChange() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {}, 300);
    },

    onPeriodFilterChange() {
      this.currentPage = 1;
      this.loadSlabs();
    },

    clearFilters() {
      this.searchQuery = "";
      this.filters.dateFrom = "";
      this.filters.dateTo = "";
      this.currentPage = 1;
      this.loadSlabs();
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadSlabs();
    },

    parseListResponse(res) {
      if (!res) return { rows: [], total: 0 };
      if (Array.isArray(res.data)) {
        return {
          rows: res.data,
          total: res.total ?? res.totalCount ?? res.data.length,
        };
      }
      if (Array.isArray(res)) {
        return { rows: res, total: res.length };
      }
      if (Array.isArray(res.items)) {
        return {
          rows: res.items,
          total: res.total ?? res.items.length,
        };
      }
      return { rows: [], total: 0 };
    },

    async loadSlabs() {
      this.dataLoading = true;
      try {
        let url =
          `${this.apiRoot()}GetByPagination?db=` +
          encodeURIComponent(this.$store.state.userData.db) +
          "&campusID=" +
          this.campusID() +
          "&pageNumber=" +
          this.currentPage +
          "&pageSize=" +
          this.perPage;
        url = this.appendPeriodQueryParams(url);
        const res = await this.get({
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        });
        const { rows, total } = this.parseListResponse(res);
        this.slabs = rows;
        this.totalCount = total;
      } catch (e) {
        console.error("SalaryTaxSlab load error:", e);
        this.slabs = [];
        this.totalCount = 0;
        this.$bvToast.toast("Failed to load tax slabs.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.dataLoading = false;
      }
    },

    incomeToInput(value) {
      if (value === null || value === undefined || value === "") return "";
      return value;
    },

    setIncomeToForm(value) {
      const trimmed = String(value ?? "").trim();
      this.form.incomeTo = trimmed === "" ? null : Number(trimmed);
    },

    setIncomeToRow(row, value) {
      const trimmed = String(value ?? "").trim();
      row.incomeTo = trimmed === "" ? null : Number(trimmed);
    },

    editSlab(item) {
      if (!this.rights.edit) return;
      this.isEdit = true;
      this.form = this.normalizeSlab(item);
      this.showModal = true;
    },

    resetForm() {
      this.form = this.emptyForm(this.filters.dateFrom, this.filters.dateTo);
    },

    validateSlab(payload) {
      if (!payload.dateFrom || !payload.dateTo) {
        this.$bvToast.toast("Period from and period to are required.", {
          title: "Validation",
          variant: "warning",
          solid: true,
        });
        return false;
      }
      if (String(payload.dateTo) < String(payload.dateFrom)) {
        this.$bvToast.toast("Period to must be on or after period from.", {
          title: "Validation",
          variant: "warning",
          solid: true,
        });
        return false;
      }
      if (payload.incomeFrom == null || Number.isNaN(Number(payload.incomeFrom))) {
        this.$bvToast.toast("Income from is required.", {
          title: "Validation",
          variant: "warning",
          solid: true,
        });
        return false;
      }
      if (
        payload.incomeTo != null &&
        payload.incomeTo !== "" &&
        Number(payload.incomeTo) < Number(payload.incomeFrom)
      ) {
        this.$bvToast.toast("Income to must be greater than income from.", {
          title: "Validation",
          variant: "warning",
          solid: true,
        });
        return false;
      }
      if (Number.isNaN(Number(payload.taxRate))) {
        this.$bvToast.toast("Tax rate is required.", {
          title: "Validation",
          variant: "warning",
          solid: true,
        });
        return false;
      }
      return true;
    },

    async saveSlab() {
      if (this.isEdit ? !this.rights.edit : !this.rights.add) return;
      const payload = {
        ...this.form,
        campusID: this.campusID(),
        incomeTo:
          this.form.incomeTo === "" || this.form.incomeTo == null
            ? null
            : Number(this.form.incomeTo),
        incomeFrom: Number(this.form.incomeFrom),
        taxRate: Number(this.form.taxRate),
        fixedTax: Number(this.form.fixedTax) || 0,
        sortOrder: Number(this.form.sortOrder) || 0,
        dateFrom: this.form.dateFrom,
        dateTo: this.form.dateTo,
      };
      if (!this.validateSlab(payload)) return;

      this.saving = true;
      try {
        const ok = await this.post({
          url:
            `${this.apiRoot()}Save?db=` +
            encodeURIComponent(this.$store.state.userData.db) +
            "&campusID=" +
            this.campusID() +
            "&userID=" +
            this.userID(),
          body: payload,
          message: this.isEdit ? "Tax slab updated." : "Tax slab created.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (ok) {
          this.showModal = false;
          this.filters.dateFrom = payload.dateFrom;
          this.filters.dateTo = payload.dateTo;
          await this.loadSlabs();
        }
      } finally {
        this.saving = false;
      }
    },

    async deleteSlab(item) {
      if (!this.rights.delete) return;
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: "This tax slab will be deleted.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete",
      });
      if (!result.isConfirmed) return;

      const ok = await this.delete({
        url:
          `${this.apiRoot()}Delete?db=` +
          encodeURIComponent(this.$store.state.userData.db) +
          "&campusID=" +
          this.campusID() +
          "&id=" +
          item.id +
          "&userID=" +
          this.userID(),
        message: "Tax slab deleted.",
        context: this,
        token: this.$store.state.userData.token,
      });
      if (ok) await this.loadSlabs();
    },

    async toggleStatus(item, isActive) {
      if (!this.rights.edit || !item.id) return;
      const previous = !!item.isActive;
      this.statusLoadingId = item.id;
      this.$set(item, "isActive", !!isActive);
      try {
        const cId = this.campusID();
        const ok = await this.put({
          url:
            `${this.apiRoot()}SetStatus/${cId}/${item.id}/status?db=` +
            encodeURIComponent(this.$store.state.userData.db) +
            "&userID=" +
            this.userID(),
          body: { isActive: !!isActive },
          message: isActive ? "Slab activated." : "Slab deactivated.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (!ok) this.$set(item, "isActive", previous);
      } catch (e) {
        this.$set(item, "isActive", previous);
      } finally {
        this.statusLoadingId = null;
      }
    },

    async openBulkModal() {
      if (!this.rights.edit && !this.rights.add) return;
      if (!this.hasPeriodFilter) {
        this.$bvToast.toast("Select period from and period to first.", {
          title: "Info",
          variant: "info",
          solid: true,
        });
        return;
      }
      this.bulkPeriod = {
        dateFrom: this.filters.dateFrom,
        dateTo: this.filters.dateTo,
      };
      this.bulkLoading = true;
      try {
        let url =
          `${this.apiRoot()}GetByPagination?db=` +
          encodeURIComponent(this.$store.state.userData.db) +
          "&campusID=" +
          this.campusID() +
          "&pageNumber=1&pageSize=500";
        url = this.appendPeriodQueryParams(url);
        const res = await this.get({
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        });
        const { rows } = this.parseListResponse(res);
        this.bulkRows = rows.map((r) => this.normalizeSlab(r));
        if (!this.bulkRows.length) {
          this.bulkRows = [];
          this.addBulkRow();
        }
        this.showBulkModal = true;
      } catch (e) {
        console.error(e);
        this.$bvToast.toast("Could not load slabs for this period.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.bulkLoading = false;
      }
    },

    addBulkRow() {
      const last = this.bulkRows[this.bulkRows.length - 1];
      const nextFrom =
        last && last.incomeTo != null && last.incomeTo !== ""
          ? Number(last.incomeTo)
          : last
            ? Number(last.incomeFrom) || 0
            : 0;
      this.bulkRows.push({
        ...this.emptyForm(this.bulkPeriod.dateFrom, this.bulkPeriod.dateTo),
        campusID: this.campusID(),
        incomeFrom: nextFrom,
        incomeTo: null,
        sortOrder: this.bulkRows.length + 1,
      });
    },

    removeBulkRow(index) {
      this.bulkRows.splice(index, 1);
    },

    async saveBulkSlabs() {
      if (!this.rights.edit && !this.rights.add) return;
      if (!this.bulkPeriod.dateFrom || !this.bulkPeriod.dateTo) {
        this.$bvToast.toast("Set period from and to before saving.", {
          title: "Validation",
          variant: "warning",
          solid: true,
        });
        return;
      }

      const payloads = this.bulkRows.map((row, index) => {
        const p = {
          ...row,
          id: row.id || 0,
          campusID: this.campusID(),
          dateFrom: this.bulkPeriod.dateFrom,
          dateTo: this.bulkPeriod.dateTo,
          incomeFrom: Number(row.incomeFrom) || 0,
          incomeTo:
            row.incomeTo === "" || row.incomeTo == null
              ? null
              : Number(row.incomeTo),
          taxRate: Number(row.taxRate) || 0,
          fixedTax: Number(row.fixedTax) || 0,
          sortOrder: Number(row.sortOrder ?? index + 1),
          isActive: row.isActive !== false,
        };
        return p;
      });

      for (const p of payloads) {
        if (!this.validateSlab(p)) return;
      }

      this.bulkSaving = true;
      try {
        let saveUrl =
          `${this.apiRoot()}SaveData?db=` +
          encodeURIComponent(this.$store.state.userData.db) +
          "&campusID=" +
          this.campusID() +
          "&userID=" +
          this.userID() +
          "&dateFrom=" +
          encodeURIComponent(this.bulkPeriod.dateFrom) +
          "&dateTo=" +
          encodeURIComponent(this.bulkPeriod.dateTo) +
          "&replaceAll=true";
        const ok = await this.post({
          url: saveUrl,
          body: payloads,
          message: "Tax slabs saved for this period.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (ok) {
          this.filters.dateFrom = this.bulkPeriod.dateFrom;
          this.filters.dateTo = this.bulkPeriod.dateTo;
          this.showBulkModal = false;
          await this.loadSlabs();
        }
      } finally {
        this.bulkSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../planner/plannerListTable.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.slab-period-text {
  font-size: 0.8125rem;
  white-space: nowrap;
}

.slab-period-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.slab-period-field {
  flex: 1 1 200px;

  label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #5e5873;
    margin-bottom: 0.35rem;
  }
}

.slab-hint {
  font-size: 0.8125rem;
  color: #5e5873;
  padding: 0.6rem 0.85rem;
  background: #faf9ff;
  border-radius: 10px;
  border: 1px solid #e0d8ff;
}

.slab-pagination-meta {
  flex-direction: column;
  gap: 0.35rem;
  text-align: center;
}

.slab-filter-period-chip {
  font-size: 0.8125rem;
  color: #5e5873;
  line-height: 1.3;

  strong {
    color: #2d1b69;
    font-weight: 700;
  }
}

.slab-filter-bar {
  width: 100%;
  gap: 0.65rem;
}

.slab-filter-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 150px;
  flex: 0 1 180px;

  &--search {
    flex: 1 1 220px;
    min-width: 180px;
  }
}

.slab-filter-label {
  margin: 0;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #a5a3ae;
}

.slab-filter-clear {
  align-self: flex-end;
  margin-bottom: 0;
}

.slab-filter-actions {
  align-self: flex-end;
}

.slab-date-wrap {
  width: 100%;

  ::v-deep .flatpickr-wrapper {
    width: 100%;
    display: block;
  }

  /* Keep period pickers visible (planner shared CSS hides altInput) */
  ::v-deep input.form-control,
  ::v-deep input.flatpickr-input,
  ::v-deep .slab-date-input {
    position: static !important;
    opacity: 1 !important;
    pointer-events: auto !important;
    width: 100% !important;
    height: 46px !important;
    min-height: 46px !important;
    border-radius: 12px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0 0.75rem !important;
    font-size: 0.875rem !important;
    font-weight: 500;
    color: #2d1b69;
    background: #fff !important;
    box-shadow: none !important;
  }

  ::v-deep input.flatpickr-input[type="hidden"],
  ::v-deep input.flatpickr-input.form-control[type="hidden"] {
    display: none !important;
  }

  ::v-deep input.form-control:focus,
  ::v-deep input.flatpickr-input:focus,
  ::v-deep .slab-date-input:focus {
    border-color: #7367f0 !important;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.1) !important;
  }
}

@media (max-width: 768px) {
  .slab-filter-field,
  .slab-filter-field--search,
  .slab-filter-actions,
  .slab-filter-clear {
    flex: 1 1 100%;
    max-width: 100%;
    min-width: 0;
  }

  .slab-filter-actions .action-btn-filter {
    width: 100%;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

/* Modals render on body — global styles */
.slab-modal {
  .modal-dialog {
    max-width: 900px;
    margin: 1.25rem auto;
  }

  .modal-content {
    border: none;
    border-radius: 16px;
    box-shadow: 0 16px 48px rgba(45, 27, 105, 0.18);
    max-height: calc(100vh - 2.5rem);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &.slab-modal--edit .modal-dialog {
    max-width: 480px;
  }
}

.slab-modal-header {
  flex-shrink: 0;
  border-bottom: 1px solid #e0d8ff;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  padding: 1rem 1.25rem;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  overflow: visible;
  position: relative;
  align-items: flex-start !important;

  .modal-title {
    flex: 1;
    min-width: 0;
    margin-right: 0.5rem;
  }

  .close {
    text-shadow: none;
    opacity: 0.65;
    margin: 0 !important;
    padding: 0.35rem 0.5rem;
    font-size: 1.5rem;
    font-weight: 400;
    line-height: 1;
    flex-shrink: 0;
    position: absolute !important;
    top: 0.75rem !important;
    right: 1rem !important;
    left: auto !important;
    float: none !important;
  }
}

.slab-modal-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  min-width: 0;
}

.slab-modal-header-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.5rem;
}

.slab-modal-close-btn {
  flex-shrink: 0;
  margin: 0;
  padding: 0.25rem 0.5rem;
  border: none;
  background: transparent;
  color: #6e6b7b;
  font-size: 1.75rem;
  font-weight: 300;
  line-height: 1;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(115, 103, 240, 0.1);
    color: #2d1b69;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.25);
  }
}

.slab-modal-title {
  font-weight: 700;
  font-size: 1.1rem;
  color: #2d1b69;
}

.slab-year-badge {
  font-size: 0.75rem;
  font-weight: 700;
}

.slab-modal-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  padding: 1rem 1.25rem;
}

.slab-modal-body--editor {
  padding: 0 !important;
  display: flex;
  flex-direction: column;
}

.slab-modal-footer {
  flex-shrink: 0;
  border-top: 1px solid #e0d8ff;
  background: #fff;
  padding: 0.85rem 1.25rem;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
}

.slab-field-input {
  border-radius: 8px !important;
  border: 1px solid #e0d8ff !important;
  font-size: 0.875rem;
  min-height: 36px;

  &:focus {
    border-color: #7367f0 !important;
    box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.12);
  }
}

.slab-editor-shell {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  max-height: min(68vh, 520px);
}

.slab-editor-top {
  flex-shrink: 0;
  padding: 1rem 1.25rem 0.75rem;
  border-bottom: 1px solid #eef0f4;
  background: #fff;
}

.slab-editor-formula {
  font-size: 0.8125rem;
  color: #64748b;
  line-height: 1.45;
  margin-bottom: 0.75rem !important;
}

.slab-editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.slab-row-count {
  font-size: 0.8125rem;
  font-weight: 500;
}

.slab-editor-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 0 1rem 1rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #c4b5fd #f3f0ff;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f8f7ff;
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c4b5fd;
    border-radius: 8px;

    &:hover {
      background: #a78bfa;
    }
  }
}

.slab-editor-table {
  width: 100%;
  min-width: 640px;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 0.75rem;

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f3f0ff;
    color: #5e5873;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.55rem 0.5rem;
    border-bottom: 1px solid #e0d8ff;
    white-space: nowrap;
  }

  tbody td {
    padding: 0.45rem 0.35rem;
    vertical-align: middle;
    border-bottom: 1px solid #f1f5f9;
  }

  tbody tr:nth-child(even) td {
    background: #fafbfc;
  }

  tbody tr:hover td {
    background: #f8f6ff;
  }

  .col-num {
    width: 36px;
    text-align: center;
    font-weight: 700;
    color: #7367f0;
    font-size: 0.8125rem;
  }

  .col-del {
    width: 44px;
    text-align: center;
  }
}

@media (max-width: 576px) {
  .slab-modal .modal-dialog {
    margin: 0.5rem;
    max-width: calc(100% - 1rem);
  }

  .slab-editor-shell {
    max-height: calc(100vh - 220px);
  }
}
</style>
